import { ChevronDownIcon } from 'components'
import { InputWithAdornment } from 'components/Form'
import { ResultsTray } from 'components/GenericSearch/ResultsTray'
import { LocationCard } from 'components/Location/LocationCard/LocationCard'
import { LocationSelectResult } from 'components/Location/LocationCard/LocationSelectResult'
import { useBoolean, useClickOutside } from 'hooks'
import { useSingleLocation } from 'hooks/locationHooks/useSingleLocation'
import { useDelayedAction } from 'hooks/useDelayedAction'
import { useCallback, useRef } from 'react'
import { Box } from 'theme-ui'
import { noop } from 'utils'
import { AdvancedInputProps, CheckboxInput } from '.'
import { BasicInputGroup } from './BasicInputGroup'
import { getFromLocalizedString } from 'utils/getFromLocalizedString'

export interface LocationSelectProps extends AdvancedInputProps {
  value?: string | null
  ids: string[]
  validateForm: () => void
}

export const LocationSelectInput = ({
  name,
  label,
  setFieldValue,
  onBlur = noop,
  validateForm,
  ids,
  disabled,
  error,
  value,
  tabIndex,
}: LocationSelectProps) => {
  const delayed = useDelayedAction()
  const isLocationConfirmed = value !== ''

  const locationSelectRef = useRef<HTMLDivElement>(null)
  const [showResults, show, hide] = useBoolean(false)
  useClickOutside(locationSelectRef, hide)

  const [selectedLocation, loading] = useSingleLocation(value ?? undefined)

  const handleItemPicked = useCallback(
    ({ id }: { id?: string }) => {
      setFieldValue(name, id)
      hide()
    },
    [setFieldValue, hide, name]
  )

  const handleLocationConfirmedChange = (isLocationConfirmed: boolean) => {
    hide()
    if (isLocationConfirmed === false) {
      setFieldValue(name, '', false)
    } else {
      setFieldValue(name, null, false)
    }
    delayed(validateForm)
  }

  const displayText = value ? (
    <LocationCard locationId={value} />
  ) : (
    'Not confirmed'
  )
  const inputText = !!selectedLocation?.name
    ? getFromLocalizedString(selectedLocation.name)
    : loading
    ? 'loading...'
    : isLocationConfirmed === false
    ? '-'
    : ''

  return (
    <BasicInputGroup
      label={label}
      name={name}
      error={error}
      disabled={disabled}
      display={displayText}
      input={
        <>
          <Box
            sx={{ position: 'relative' }}
            onBlur={onBlur}
            ref={locationSelectRef}
          >
            <InputWithAdornment
              id={name}
              name={name}
              value={inputText}
              aria-invalid={!!error}
              disabled={disabled}
              tabIndex={tabIndex}
              onFocus={show}
              sx={{ cursor: 'pointer' }}
              after={<ChevronDownIcon />}
              isAfterClickable={false}
              readOnly
            />
            <ResultsTray
              ItemCard={LocationSelectResult}
              variant="select"
              results={ids.map((id) => ({ id }))}
              show={showResults}
              isLoading={loading}
              onSearchItemClick={handleItemPicked}
              noResultsText="You have not added any teaching locations to your profile yet."
            />
          </Box>
          <CheckboxInput
            label="Not Confirmed"
            value={!isLocationConfirmed}
            name="locationNotConfirmed"
            onBlur={onBlur}
            setFieldValue={(name: any, isNotConfirmed: boolean) => {
              handleLocationConfirmedChange(!isNotConfirmed)
            }}
            tabIndex={tabIndex ? tabIndex + 0.1 : undefined}
          />
        </>
      }
    />
  )
}
